import { useCallback, useEffect, useState } from 'react';
import { BellRing, BellOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPushState, enablePush, disablePush, sendTestPush } from '../../utils/push';

// Phone, Settings → Notifications: one switch that turns alerts on this phone on or off.
// Renders nothing on the website (push only exists inside the phone app).
export const PushToggle = () => {
  const [state, setState] = useState(null);   // null while checking
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => setState(await getPushState()), []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!state || state === 'unsupported') return null;

  const on = state === 'on';
  const blocked = state === 'denied';

  const flip = async () => {
    if (busy || blocked) return;
    setBusy(true);
    if (on) {
      await disablePush();
      toast('Push notifications are off');
    } else {
      const r = await enablePush();
      if (r.ok) toast.success('Push notifications are on');
      else if (r.reason === 'denied') toast.error('Notifications are blocked in your phone settings');
      else toast.error('Could not turn on notifications. Please try again.');
    }
    setBusy(false);
    refresh();
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      if (r?.sent > 0) toast.success('Test sent. It should arrive in a few seconds.');
      else toast.error(r?.message || 'No phone is registered for notifications yet.');
    } catch (e) {
      toast.error(e.message || 'Could not send the test');
    }
    setBusy(false);
  };

  return (
    <div className={`m-pushtoggle${blocked ? ' is-blocked' : ''}`}>
      <div className="m-pt-row">
        <span className="m-pt-icon">{blocked ? <BellOff size={22} /> : <BellRing size={22} />}</span>
        <span className="m-pt-text">
          <span className="m-pt-title">Push notifications</span>
          <span className="m-pt-sub">
            {blocked
              ? 'Blocked in your phone settings'
              : on ? 'On for this phone' : 'Off. Turn on to get alerts on this phone'}
          </span>
        </span>
        <button
          type="button" role="switch" aria-checked={on} aria-label="Push notifications"
          className={`m-switch${on ? ' is-on' : ''}`} disabled={busy || blocked} onClick={flip}
        ><span className="m-switch-knob" /></button>
      </div>
      {blocked && (
        <div className="m-pt-help">
          To get alerts, open your phone's Settings, then Apps, then Car Collector Studio, then Notifications, and switch them on.
        </div>
      )}
      {on && (
        <button type="button" className="m-pt-test" disabled={busy} onClick={test}>Send me a test notification</button>
      )}
      <div className="m-pt-foot">We let you know on this phone when insurance, road tax, an inspection or a service is about to expire.</div>
    </div>
  );
};
